import React, {useRef, useEffect} from 'react';
import {GeneratedVideoChunk} from '../../../data/models/LibraryFile/GeneratedVideoChunk';
import styles from './ChunkPreview.module.css';
import {VideoEditorText} from '../VideoEditorText';

// Extract base URL without query parameters (signed URLs have different signatures each time)
const getBaseUrl = (url: string | undefined): string => {
  if (!url) return '';
  const questionMarkIndex = url.indexOf('?');
  return questionMarkIndex === -1 ? url : url.substring(0, questionMarkIndex);
};

interface Props {
  selectedChunk?: GeneratedVideoChunk;
  onPlayFinished?: () => void;
}

const ChunkPreview: React.FC<Props> = ({selectedChunk, onPlayFinished}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const currentBaseUrlRef = useRef<string>('');
  const currentChunkIdRef = useRef<string>('');

  useEffect(() => {
    if (!selectedChunk || !videoRef.current) return;

    const newBaseUrl = getBaseUrl(selectedChunk.uri);
    const chunkChanged = selectedChunk.id !== currentChunkIdRef.current;
    const urlChanged = newBaseUrl !== currentBaseUrlRef.current;

    // Only update video src if chunk changed or base URL changed
    if (chunkChanged || urlChanged) {
      currentBaseUrlRef.current = newBaseUrl;
      currentChunkIdRef.current = selectedChunk.id;
      videoRef.current.src = selectedChunk.uri;
      videoRef.current.load();
    }
  }, [selectedChunk]);

  // Handle initial mount or when chunk becomes undefined
  useEffect(() => {
    if (!selectedChunk) {
      currentBaseUrlRef.current = '';
      currentChunkIdRef.current = '';
    }
  }, [selectedChunk]);

  return selectedChunk ? (
    <div className={styles.chunksPreviewContainer}>
      <video
        ref={videoRef}
        controls
        onEnded={onPlayFinished}
        className={styles.videoPlayer}
        tabIndex={-1}
      />
    </div>
  ) : (
    <div className={styles.empty}>
      <p>{VideoEditorText.emptyChunkPreview}</p>
    </div>
  );
};

export default React.memo(ChunkPreview);
